import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import dayjs from 'dayjs';
import { SHARED_ENVIRONMENT } from '@cp/shared/environment/environment.token';
import { Environment } from '@cp/shared/environment/environment.interface';

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  // note: https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
  // note: experimental, only for chromium!
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _event: any;
  private _isPwaBanner = new BehaviorSubject(false);
  public isPwaBanner$ = this._isPwaBanner.asObservable();

  constructor(
    private _cookieService: CookieService,
    @Inject(SHARED_ENVIRONMENT) private _environment: Environment,
  ) {}

  public onEvent(e: Event): void {
    if (!this._environment.production)
      console.log(`[DEBUG] PwaService.onEvent`);
    this._event = e;
    if (this._isCorrectDisplayMode() && this._event !== null) {
      this._isPwaBanner.next(!this._wasPwaBannerDismissed());
      if (!this._environment.production)
        console.log(
          `[DEBUG] PwaService.onEvent isDismissed:${this._wasPwaBannerDismissed()} _isPwaBanner:${!this._wasPwaBannerDismissed()}`,
        );
    }
  }

  public dismissBanner(): void {
    const expireAt = dayjs().add(30, 'days');
    this._cookieService.set('PWA_DISMISSED', 'true', {
      expires: expireAt.toDate(),
    });
    this._isPwaBanner.next(false);
    if (!this._environment.production)
      console.log(`[DEBUG] PwaService.dismissBanner _isPwaBanner:false`);
  }

  public unsetEvent(): void {
    this._event = null;
    this._isPwaBanner.next(false);
    if (!this._environment.production)
      console.log(`[DEBUG] PwaService.unsetEvent _isPwaBanner:false`);
  }

  public async onInstall(): Promise<void> {
    if (!this._environment.production)
      console.log(`[DEBUG] PwaService.onInstall`);
    if (!this._event) {
      return;
    }

    this._event.prompt();
    const wasInstalled = await this._event.userChoice.then(
      (result: { outcome: 'accepted' | 'dismissed' }) => {
        this._event = null;
        return result.outcome === 'accepted';
      },
    );
    this._isPwaBanner.next(!wasInstalled);
    if (!this._environment.production)
      console.log(
        `[DEBUG] PwaService.onInstall wasInstalled:${wasInstalled} _isPwaBanner:${!wasInstalled}`,
      );
  }

  private _isCorrectDisplayMode(): boolean {
    return !window.matchMedia('(display-mode: standalone)').matches;
  }

  private _wasPwaBannerDismissed(): boolean {
    const cookie = this._cookieService.get('PWA_DISMISSED') || '';
    return Boolean(cookie);
  }
}
